import React, { useMemo } from 'react';
import {
  Drawer, Box, Typography, IconButton, Paper, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { generateBodyHtml } from './bodyHtmlGenerator';

const DRAWER_WIDTH = '42vw';

/**
 * A right-anchored drawer showing a live preview of the listing (Shopify Body HTML).
 *
 * Props:
 *   open            — boolean
 *   onClose         — () => void
 *   product         — product object (consolidatedData, gptDimensionsData, priceOptions)
 *   template        — CategoryConfig.bodyHtmlTemplate (or null for auto-detect)
 */
export default function BodyHtmlDrawer({ open, onClose, product, template }) {
  const liveHtml = useMemo(() => {
    if (!product) return '';
    return generateBodyHtml(
      product.specs || product.consolidatedData || {},
      product.gptDimensionsData || null,
      product.priceOptions || null,
      template || null,
    );
  }, [product, template]);

  const handleCopy = () => {
    navigator.clipboard.writeText(liveHtml).catch(() => {});
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH, maxWidth: 640, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
          Listing Preview
        </Typography>
        <Tooltip title="Copy HTML">
          <IconButton size="small" onClick={handleCopy} sx={{ mr: 0.5 }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Rendered preview */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box
            dangerouslySetInnerHTML={{ __html: liveHtml }}
            sx={{
              fontSize: '0.9rem',
              lineHeight: 1.6,
              '& ul': { pl: 2.5 },
              '& li': { mb: 0.5 },
              '& p': { mt: 0, mb: 1 },
              '& strong': { fontWeight: 600 },
            }}
          />
        </Paper>
      </Box>
    </Drawer>
  );
}
